/**
 * Overlay-engine shared types (M9 Demographics & Census overlays).
 *
 * The overlay engine renders public-data census/demographic choropleths on a
 * user's map, gated behind a paid plan. Every country source (US Census,
 * StatCan, UK postcode) is a swappable {@link DemographicProvider} behind ONE
 * engine — mirroring the geo-core routing vendor-abstraction (`@/lib/geo/routing`).
 * These types are the vendor-neutral vocabulary the engine, the classification
 * engine, the geo-join service, and the render layer all share.
 *
 * Pure + dependency-light: only a geometry `Ring` type is borrowed from the M8
 * spatial core so the point-in-polygon geo-join reuses `pointInRing` rather than
 * re-implementing it. No `pg`, no maplibre — so the whole engine is unit-testable
 * against a fixture provider with no database.
 */

import type { Ring } from "@/lib/analysis/geometry";

/** Countries the milestone scopes (per M9.md — no forced cross-country normalization). */
export const OVERLAY_COUNTRIES = ["US", "CA", "UK"] as const;
export type Country = (typeof OVERLAY_COUNTRIES)[number];

/**
 * The public data source behind an overlay. One source per country adapter; the
 * source is persisted on the `overlay` block so a saved map redraws through the
 * SAME adapter it was authored with.
 */
export const OVERLAY_SOURCES = ["us_census", "statcan", "uk_postcode"] as const;
export type OverlaySource = (typeof OVERLAY_SOURCES)[number];

/**
 * Boundary geography granularity ("tier"). Free-form string, NOT an enum: each
 * source self-describes its tiers (US: state/county/zcta/tract; CA/UK differ) so
 * the engine never hardcodes one country's geography vocabulary.
 */
export type Tier = string;

/** A data vintage tag (e.g. a census year) — the ETL swap key; opaque to the engine. */
export type Vintage = string;

/**
 * One catalog entry: a demographic variable a source exposes. `sourceCitation`
 * feeds the M11 AttributionResolver (public-data credit); `label`/`units` are the
 * human-readable strings the variable picker shows. Per-country by construction —
 * the catalog stores each source's own labels/units, never a normalized union.
 */
export interface DemographicVariable {
  id: string;
  country: Country;
  /** Human-readable label (from the source's own metadata). */
  label: string;
  /** Units string (e.g. "people", "USD", "%"). */
  units: string;
  /** Public-data attribution line (surfaced by M11 compliance). */
  sourceCitation: string;
  /** Tiers this variable is published at; empty/undefined = all of the source's tiers. */
  tiers?: Tier[];
}

/**
 * A boundary polygon at a tier + vintage. `geometry` is a MultiPolygon flattened
 * to a list of outer rings ([lng,lat] vertices) — the simplified render/join
 * geometry. (Topology-preserving simplification + multi-zoom vector tiling of the
 * full-resolution source polygons is an M9-B concern; the shape is frozen here.)
 */
export interface Boundary {
  id: string;
  country: Country;
  tier: Tier;
  vintage: Vintage;
  /** Display name (e.g. county name); optional — not every source names every geography. */
  name?: string;
  /** One or more outer rings; a point is "in" the boundary if inside ANY ring. */
  geometry: Ring[];
}

/** A single demographic value for a (boundary, variable, vintage). */
export interface DemographicDatum {
  boundaryId: string;
  variableId: string;
  vintage: Vintage;
  /** The numeric value (null when the source suppressed/omitted it). */
  value: number | null;
}

/** One choropleth class: its lower-edge value + the TOKEN-KEYED ramp color for it. */
export interface ChoroplethStop {
  /** Lower-edge value of the class (matches the corresponding break). */
  value: number;
  /**
   * Token id for the class color — NEVER a raw hex. Resolved to a concrete hex
   * at render time from the compiled token artifact via `resolveStopColor`.
   */
  tokenId: string;
}

/**
 * The classification result the engine hands the render layer + legend: the
 * ascending break edges and the per-class token-keyed color stops. `breaks` has
 * `stops.length + 1` entries (class edges); `stops[i]` colors the class
 * `[breaks[i], breaks[i+1])`.
 */
export interface Choropleth {
  method: ClassificationMethod;
  breaks: number[];
  stops: ChoroplethStop[];
}

/** Classification methods the engine supports (M9 slice). */
export const CLASSIFICATION_METHODS = ["quantile", "equal", "manual"] as const;
export type ClassificationMethod = (typeof CLASSIFICATION_METHODS)[number];

/** A classification request: method + class count, or explicit manual break edges. */
export interface Classification {
  method: ClassificationMethod;
  /** Number of classes; ignored for `manual` (derived from `breaks`). Defaults to 5. */
  classes?: number;
  /** Explicit ascending break edges — REQUIRED for `manual`, ignored otherwise. */
  breaks?: number[];
  /** Ramp family token id (e.g. `map.ramp.sequential`); defaults to sequential. */
  rampTokenId?: string;
}
